import { isObject } from '@core/isObject'
import { removeNullish } from '@core/removeNullish'
import { logger } from '@core/logger'

/**
 * Parses a query string to an object.
 * @param {string} input - The query string being processed.
 * @returns {Record<string, string>}
 */
export function parseQueryString(input: string) {
  try {
    const searchParams = new URLSearchParams(input)

    // `fromEntries` also removed duplicate values.
    return Object.fromEntries(searchParams.entries())
  } catch (error) {
    logger.error('QUERY_STRING_PARSE', error)
  }
}

interface Options {
  addQueryPrefix?: boolean
}

export type QueryStringInput =
  | Record<string, string | string[] | null | undefined | number>
  | [string, string][]

/**
 * Converts an object or and array into a query string.
 *
 * Examples:
 *  - `{foo: 'x'}` will give `?foo=x`
 *  - `[['foo', 'x']]` will give `?foo=x`
 *  - `[['foo', 'x'], ['foo', 'y']]` will give `?foo=x&foo=y`
 *
 */
export function convertToQueryString(
  input: QueryStringInput,
  options: Options = {}
): string | undefined {
  const defaults = { addQueryPrefix: false }
  const opts = { ...defaults, ...options }

  try {
    // Remove `undefined`, `null`, `[]`, and `"""` et al, but keep zeros.
    const cleanedInput = isObject(input) ? removeNullish(input) : input
    const searchParams = new URLSearchParams(cleanedInput)

    // As the order of the parameters is inconsistent between Safari/Node, we
    // manually sort the keys alphabetically to ensure that query strings are
    // predictable and won’t cause server-side/client-side mismatches.
    searchParams.sort()

    const stringifiedParams = searchParams.toString()
    const prefix = opts.addQueryPrefix && stringifiedParams ? '?' : ''

    return prefix + stringifiedParams
  } catch (error) {
    logger.error('QUERY_STRING_STRINGIFY', error)
  }
}
